/* global process */
import screenfull from 'screenfull'
import { call, put, select, takeEvery, takeLatest, fork, delay } from 'redux-saga/effects'
// import { loader as PixiLoader } from 'pixi.js'
import Api from '../Api'
import * as actions from './game.actions'
import * as authActions from '../Auth/auth.actions'
import * as preloaderActions from '../Preloader/preloader.actions'
import * as preloaderSelectors from '../Preloader/preloader.selectors'
import * as savePointActions from '../SavePoint/savePoint.actions'
import * as savePointSelectors from '../SavePoint/savePoint.selectors'
import * as selectors from './game.selectors'
import * as i18nSelectors from '../i18n/i18n.selectors'
import * as videoPlayerActions from '../VideoPlayer/videoPlayer.actions'
import * as Errors from '../Errors'
import * as audio from './AudioContainer/audio.sagas'
import * as loaderActions from '../Loader/loader.actions'
import DeviceInfo from '../DeviceInfo'

/**
 *  Show the next scene for media scene.
 */
function* finishMediaPlayback() {
  const currentScene = yield select(selectors.getCurrentScene)
  const nextSceneId = currentScene.nextScene

  yield put(actions.startSceneChange(nextSceneId))

  if (currentScene.videoOptions) {
    const elementsStatuses = yield select(savePointSelectors.getElementsStatuses)
    const status = elementsStatuses[currentScene.id] ? elementsStatuses[currentScene.id] : 'default'

    if (currentScene.videoOptions[status] && currentScene.videoOptions[status].action) {
      let gameAction = currentScene.videoOptions[status].action
      yield put(actions.addAction(gameAction))
    }
    yield put(
      savePointActions.updateObjectStatus({
        id: currentScene.id,
        status: 'viewed',
      })
    )
  }
}

/**
 *  Calculates the device info and update the store
 */
function* getDeviceInfo() {
  const deviceInfo = new DeviceInfo()
  const windowRatio = deviceInfo.getWindowRatio()
  const { browser } = deviceInfo.info
  const isChrome = browser.name.match(/chrome/i)

  yield put(actions.setDeviceInfo(deviceInfo.toObject()))

  if (windowRatio > 3.0 / 4.0) {
    yield put(actions.showError(Errors.PORTRAIT_ERROR))
  } else {
    yield put(actions.hideError(Errors.PORTRAIT_ERROR))
  }
}

/**
 *  Send GAME_INIT action
 */
function* initGame() {
  const savePoint = yield select(savePointSelectors.getSavePoint)
  const showConsumeWarning = savePoint.showConsumeWarning

  const deviceInfo = new DeviceInfo()
  yield put(actions.syncWithSavePoint(savePoint))

  if (deviceInfo.info.device.type == 'mobile' && showConsumeWarning) {
    yield put(actions.showError(Errors.DATA_CONSUME))
  } else {
    yield put(actions.initGame())
  }
}

/**
 *  Perform the scene change process.
 *  @param   {object}     action  Redux action
 */
function* loadScene(action) {
  // Begin scene transition animation
  yield put(actions.showSceneTransition())

  let nextSceneId = action.data

  // Is possible that auth module init a scene change without nextSceneId.
  // In this case we assume that nextSceneId was set by user info
  // synchronization
  if (!nextSceneId) {
    const game = yield select(selectors.getGame)
    nextSceneId = game.currentSceneId
  }

  yield put(actions.startScenePreload(nextSceneId)) //PRELOAD CURRENT
}

/**
 * FINISH Current Preload -> transition and init LazyLoad
 */
function* loadedCurrentFinished(action) {
  const sceneId = action.data
  yield put(actions.finishSceneChange(sceneId))
  yield delay(500) // Fade Animation length
  yield put(actions.hideSceneTransition())
  yield delay(500)
  // TODO do we really need to preload the next scene here ? It is already done upon starting to change the scene
  yield put(actions.startNextScenesLazyload(sceneId)) //PRELOAD NEXT ASSETS
}

/**
 *  Show login error, wait some time and redirect to platform.
 *  @param   {object}     action  Redux action
 */
function* loginError() {
  yield put(actions.showError(Errors.LOGIN_ERROR))
  yield delay(4000)
  yield put(actions.returnToPlatform())
}

/**
 *  Take care about window rezise. We will wait for 400 ms before send finish
 *  resize signal.
 */
function* resize() {
  yield delay(400)

  yield put(actions.finishResize())
}

/**
 *  Load the student platform
 */
function* returnToPlatform() {
  let loginUrl = process.env.LOGIN_URL
  try {
    console.info('logout...')
    const gameToken = window.SessionStorage.getItem('gameToken')
    window.SessionStorage.clear()
    const logoutInfo = yield Api.get(process.env.LOGOUT_URL, {
      headers: { 'x-auth-logout': gameToken },
    })
    loginUrl = logoutInfo && logoutInfo.loginUrl ? logoutInfo.loginUrl : loginUrl
    window.location.assign(logoutInfo.loginUrl)
  } catch (error) {
    console.error('Error while logging out', error)
  }
  console.error('Redirecting to login...')
  window.location.assign(loginUrl)
}

/**
 *  Load scene preload indication.
 */
function* startNextScenesLazyload(action) {
  const sceneId = action.data
  yield makeLoad(sceneId, false)
}

/**
 *  Load the scene itself.
 */
function* startScenePreload(action) {
  const sceneId = action.data
  yield makeLoad(sceneId, true)
}

/**
 *  Start the assets load.
 *  @param   {object}  action  Redux action
 */
function* makeLoad(sceneId, isCurrent) {
  const translations = yield select(i18nSelectors.getI18nMessages)
  const storyLine = yield select(selectors.getStoryLine)
  const commonPreload = storyLine.commonPreload || {}
  const scenes = storyLine.scenes
  const scene = scenes[sceneId]
  const preloaderSettings = yield select(preloaderSelectors.getPreloaderSettings)

  if (!scene) {
    throw new Error(`No scene identified with ${sceneId}.`)
  }

  // Load the commons if we are loading the current scene, otherwise, load the
  // preload scenes
  let toPreload = isCurrent ? commonPreload : {}
  const preloadScenes = isCurrent ? [scene.id] : (scene.preload || []).slice()

  for (let preloadSceneId of preloadScenes) {
    const preloadScene = scenes[preloadSceneId]

    toPreload = {
      ...toPreload,
      ...preloadBackgrounds(preloadScene, storyLine),
      ...preloadObjectResources(preloadScene, storyLine),
      ...preloadSceneMedia(preloadScene, storyLine, translations, preloaderSettings),
      ...preloadSounds(preloadScene, storyLine),
      ...preload3DModels(preloadScene, storyLine),
    }
  }
  // PixiLoader.load() //not needed callback
  yield put(
    preloaderActions.startPreload({
      toPreload,
      isCurrent,
      sceneId,
    })
  )
}

function preloadSceneMedia({ media }, { media: storyLineMedia = {} }, translations, settings) {
  const multilang = settings.multilang
  let sceneMediaUrl

  if (media) {
    if (multilang) {
      sceneMediaUrl = translations ? translations[`kinematics.${media}`] : null
    } else {
      sceneMediaUrl = (storyLineMedia[media] || {}).urlTemplate || ''
    }
  }

  if (sceneMediaUrl) {
    return {
      [media]: { id: media, type: 'media', url: sceneMediaUrl },
    }
  }

  return {}
}

function preloadBackgrounds({ backgrounds = [] }, { backgrounds: storyLineBackgrounds = {} }) {
  let toPreload = {}

  for (let id of backgrounds) {
    const background = storyLineBackgrounds[id]
    const url = background.urlTemplate

    if (!url) {
      continue
    }
    toPreload = {
      ...toPreload,
      [id]: { id, type: 'image', url },
    }
  }

  return toPreload
}

function preloadSounds({ sounds = [] }, { sounds: storyLineSounds = {} }) {
  let toPreload = {}

  for (let id of sounds) {
    const sound = storyLineSounds[id]
    let url = sound.urlTemplate
    // TODO: determine if replacements are necesary
    toPreload = {
      ...toPreload,
      [id]: { id, type: 'sound', url },
    }
  }
  return toPreload
}

function preload3DModels({ buildings = [] }, { buildings: storyLineBuildings = {} }) {
  let toPreload = {}
  for (let id of buildings) {
    const elem = storyLineBuildings[id]
    let assets = elem.assets
    for (let i in assets) {
      toPreload = {
        ...toPreload,
        [id + '_' + i]: { id: id + '_' + i, type: 'models', url: assets[i] },
      }
    }
  }
  return toPreload
}

function preloadObjectResources({ objects = [] }, storyLine) {
  let toPreload = {}
  for (let objectId of objects) {
    const { sceneObjects: storyLineObjects = {} } = storyLine
    const object = storyLineObjects[objectId]
    toPreload = {
      ...toPreload,
      ...preloadBackgrounds(object, storyLine),
      ...preloadSounds(object, storyLine),
    }
  }
  return toPreload
}

/**
 *  Show media in popup
 */
function* showMedia() {
  yield put(actions.showMedia())
}

/**
 * Start screen remove.
 */
function* startRemoveScreen(action) {
  const screenId = action.data

  yield delay(600)
  yield put({ type: actions.REMOVE_SCREEN_FINISH, data: screenId })
}

/**
 * Change scene by action 'change_scene'.
 */
function* changeSceneByAction(action) {
  const gameAction = action.data
  if (gameAction && gameAction.type && gameAction.type === 'change_scene') {
    yield put(actions.startSceneChange(gameAction.value))
  }
}
/**
 * Start screen add.
 */
function* startAddScreen(action) {
  const screenId = action.data

  yield delay(600)
  yield put({ type: actions.ADD_SCREEN_FINISH, data: screenId })
}

/**
 *  Reload the entire game.
 */
function reload() {
  window.location.reload(true)
}

/**
 *  Enter in fullscreen mode.
 */
function* goFullscreen() {
  const promptFullscreen = yield select(selectors.getPromptFullscreen)

  if (promptFullscreen) {
    yield put(actions.promptFullscreen())
  }
}

/**
 *  Request fullscreen mode.
 */
function promptFullscreen() {
  if (screenfull.enabled) {
    screenfull.request()
  }
}
/**
 *
 */
function* removeCurrentAudio(action) {
  const audioId = action.data
  const audiosPlaying = yield select(selectors.getAudiosPlaying)
  const currentAudio = audiosPlaying[audioId]

  if (currentAudio && currentAudio.audio && !currentAudio.audio.paused) {
    currentAudio.onEnded() //Force Ended
  }
}

function* addLoader(action) {
  const loader = action.data
  if (loader == 'session_ended_loader') {
    yield put(actions.addPopup('new_session_popup'))
  }
}

function* removeLoader(action) {
  const loader = action.data
  if (loader == 'session_ended_loader') {
    yield put(actions.removePopup('new_session_popup'))
  }
}

/**
 *  Game sagas entrypoint
 */
export function* sagas() {
  yield getDeviceInfo()

  yield takeEvery(authActions.LOGIN_FINISH_ERROR, loginError)
  yield takeEvery(authActions.USER_INFO_FINISH_ERROR, loginError)
  yield takeEvery(savePointActions.LOADED, initGame)
  yield takeEvery(actions.CHANGE_SCENE_START, loadScene)
  yield takeLatest(actions.RESIZE_START, resize)
  yield takeEvery(actions.RETURN_TO_PLATFORM, returnToPlatform)
  yield takeEvery(actions.RESIZE_FINISH, getDeviceInfo)
  yield takeEvery(actions.SCENE_PRELOAD_START, startScenePreload)
  yield takeEvery(actions.NEXT_SCENES_LAZYLOAD_START, startNextScenesLazyload)
  yield takeEvery(preloaderActions.FINISH_CURRENT_SCENE_PRELOAD, loadedCurrentFinished)
  yield takeEvery(videoPlayerActions.SHOW_MEDIA, showMedia)
  yield takeEvery(videoPlayerActions.FINISH_MEDIA, finishMediaPlayback)
  yield takeEvery(actions.REMOVE_SCREEN_START, startRemoveScreen)
  yield takeEvery(actions.ADD_SCREEN_START, startAddScreen)
  yield takeEvery(actions.ADD_ACTION, changeSceneByAction)
  yield takeEvery(actions.RELOAD, reload)
  yield takeEvery(actions.UNIVERSAL_CLICK, goFullscreen)
  yield takeEvery(actions.PROMPT_FULLSCREEN, promptFullscreen)
  yield takeEvery(actions.REMOVE_AUDIO, removeCurrentAudio)
  yield takeEvery(loaderActions.ADD_LOADER, addLoader),
    yield takeEvery(loaderActions.REMOVE_LOADER, removeLoader),
    yield fork(audio.sagas)
}
